<script lang="ts">
  import { Link } from 'svelte-routing';
  import Chart from '../charts/Chart.svelte';
  import Readout from '../charts/Readout.svelte';
  import { api } from '../../api/client';
  import { niceTicks, polyline } from '../../utils/chart';
  import { calendar, monthBefore, monthName, monthSentence, type MonthData } from '../../utils/insights';
  import { t } from '../../i18n/index.ts';

  // MonthlySection compares this month's activities so far with last month's
  // by the same day, with a running count through each (spec 006 FR-071),
  // and offers last month's recap (006 FR-072). The chart is in the shared chart
  // language (spec 010 FR-033), marked each week of the month.
  const THIS_COLOR = 'var(--theme-accent)';
  const PRIOR_COLOR = 'var(--theme-overlay1)';

  let m = $state<MonthData | null>(null);
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let error = $state('');

  api.get<MonthData>(`/me/months?${calendar()}`).then((got) => (m = got)).catch((e) => (error = e.message));

  let days = $derived(m ? Math.max(m.prior_running.length, m.running.length, 28) : 31);
  let scale = $derived(niceTicks(0, m ? Math.max(...m.running, ...m.prior_running, 1) : 1, { zero: true, whole: true }));
  let positions = $derived(Array.from({ length: days }, (_, i) => i / (days - 1)));
  let shown = $derived(hover ?? at);
  const line = (vs: number[]) => polyline(vs.map((v, i) => [positions[i], v] as [number, number]), scale.lo, scale.hi);
  const count = (vs: number[], i: number) => (i < vs.length ? String(vs[i]) : '–');
  let reading = $derived(m && shown != null ? t('activitiesMonthlyReading', { day: shown + 1, now: count(m.running, shown), prior: count(m.prior_running, shown) }) : '');
</script>

<section class="card p-4 space-y-3" aria-labelledby="month-heading">
  <h2 id="month-heading" class="font-semibold">{t('activitiesMonthly')}</h2>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if m}
    <p>{monthSentence(m)}</p>
    <Readout title={shown != null ? t('activitiesMonthlyDay', { day: shown + 1 }) : t('activitiesMonthlySoFar')}
      items={[
        { label: t('activitiesMonthlyThis'), value: count(m.running, shown ?? m.running.length - 1), color: THIS_COLOR },
        { label: t('activitiesMonthlyLast'), value: count(m.prior_running, shown ?? m.running.length - 1), color: PRIOR_COLOR, mark: 'dash' },
      ]} />
    <Chart lo={scale.lo} hi={scale.hi} unit={t('activitiesUnitActivities')} yTicks={scale.ticks.map((tick) => ({ v: tick, text: String(tick) }))}
      xTicks={[0, 7, 14, 21, 28].filter((d) => d < days).map((d) => ({ at: positions[d], text: t('activitiesMonthlyDay', { day: d + 1 }) }))} height="h-32" {positions} bind:at bind:hover
      label={t('activitiesMonthlyLabel')} valueText={reading || t('activitiesNoDayPicked')}
      dots={m && shown != null ? [...(shown < m.prior_running.length ? [{ v: m.prior_running[shown], color: PRIOR_COLOR }] : []), ...(shown < m.running.length ? [{ v: m.running[shown], color: THIS_COLOR }] : [])] : []}>
      {#snippet plot()}
        <polyline points={line(m!.prior_running)} fill="none" stroke={PRIOR_COLOR} stroke-width="2" stroke-dasharray="6 4" vector-effect="non-scaling-stroke" />
        <polyline points={line(m!.running)} fill="none" stroke={THIS_COLOR} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
      {/snippet}
    </Chart>
    <Link to="/activities/recap/{monthBefore(m.month)}" class="btn btn-secondary btn-sm inline-flex items-center gap-1">
      <i aria-hidden="true" class="las la-calendar-check"></i>{t('activitiesMonthlyRecap', { month: monthName(monthBefore(m.month)) })}
    </Link>
  {/if}
</section>
